<template>
  <component
    :is="tag"
    :type="type"
    v-bind="buttonProps"
    class="obutton"
    :class="[rootClasses, tag !== 'button' ? 'link inline-block' : '']"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <div v-if="iconLeft" class="obutton-icon obutton-icon-left">
      <OSvg :class="iconClasses" class="w-3" :svg="iconLeft" />
    </div>
    <span class="relative flex items-center justify-center">
      <slot />
    </span>
    <div v-if="iconRight" class="obutton-icon obutton-icon-right">
      <OSvg :class="iconClasses" class="w-3" :svg="iconRight" />
    </div>
  </component>
</template>

<script>
import { ButtonSizes, ButtonTypes, ButtonVariants } from '@/enums/components'
const OSvg = () => import('@/components/OSvg')

export default {
  name: 'OButton',
  components: { OSvg },
  props: {
    tag: {
      type: String,
      default: 'button',
      validator: value => ['button', 'a', 'nuxt-link'].includes(value)
    },
    attribute: {
      type: Object,
      default: null,
      validator: value => ['name', 'value'].every(prop => value.hasOwnProperty(prop))
    },
    variant: {
      type: String,
      default: ButtonVariants.primary,
      validator: value => Object.values(ButtonVariants).includes(value)
    },
    buttonType: {
      type: String,
      default: ButtonTypes.solid,
      validator: value => Object.values(ButtonTypes).includes(value)
    },
    type: {
      type: String,
      default: 'button',
      validator: value => ['button', 'submit'].includes(value)
    },
    to: {
      type: [String, Object],
      default: null
    },
    size: {
      type: String,
      default: ButtonSizes.medium,
      validator: value => Object.values(ButtonSizes).includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    iconRight: {
      type: String,
      default: null
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconClasses: {
      type: String,
      default: ''
    },
    ariaLabel: {
      type: String,
      default: ''
    }
  },
  computed: {
    rootClasses () {
      return [
        `obutton-${this.variant}`,
        `obutton-type-${this.buttonType}`,
        `obutton-size-${this.size}`,
        {
          'obutton-disabled': this.disabled,
          'obutton-icon-right': this.iconRight !== null,
          'obutton-icon-left': this.iconLeft !== null
        }
      ]
    },
    buttonProps () {
      const buttonProps = {
        ...(this.attribute ? { [this.attribute.name]: this.attribute.value } : {}),
        ...(this.ariaLabel ? { 'aria-label': this.ariaLabel } : {})
      }

      switch (this.tag) {
        case 'button': buttonProps.type = this.type
          break
        case 'nuxt-link': buttonProps.to = this.to
          break
        case 'a': buttonProps.href = this.to
          break
      }

      return buttonProps
    }
  }
}
</script>

<style lang="postcss" scoped>
.obutton {
  @apply relative rounded-full transition ease-in duration-300 font-body font-semibold text-white text-center leading-tight;

  &.obutton-icon-right, &.obutton-icon-left {
    @apply flex items-stretch;

    span {
      margin-top: -0.2rem;
    }
  }

  &.link {
    @apply leading-loose;
  }

  &-primary {
    &.obutton-type {
      &-solid {
        @apply bg-primary;

        &:hover {
          @apply bg-primary-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply bg-primary-light;
        }
      }

      &-border {
        @apply border-2 border-primary text-primary;

        &:hover {
          @apply text-primary-dark border-primary-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply text-primary-light border-primary-light;
        }
      }

      &-link {
        @apply text-primary;

        &:hover {
          @apply text-primary-dark;
        }

        &.obutton-disabled {
          @apply text-primary-light;
        }
      }
    }
  }

  &-secondary {
    @apply text-neutral-800;

    &:focus {
      outline: none;
    }

    &.obutton-type {
      &-solid {
        @apply bg-secondary;

        &:hover {
          @apply bg-secondary-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply bg-secondary-light;
        }
      }

      &-border {
        @apply border-2 border-secondary text-secondary;

        &:hover {
          @apply text-secondary-dark border-secondary-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply text-secondary-light border-secondary-light;
        }
      }

      &-link {
        @apply text-secondary;

        &:hover {
          @apply text-secondary-dark;
        }

        &.obutton-disabled {
          @apply text-secondary-light;
        }
      }
    }
  }

  &-success {
    &.obutton-type {
      &-solid {
        @apply bg-success;

        &:hover {
          @apply bg-success-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply bg-success-light;
        }
      }

      &-border {
        @apply border-2 border-success text-success;

        &:hover {
          @apply text-success-dark border-success-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply text-success-light border-success-light;
        }
      }

      &-link {
        @apply text-success;

        &:hover {
          @apply text-success-dark;
        }

        &.obutton-disabled {
          @apply text-success-light;
        }
      }
    }
  }

  &-info {
    &.obutton-type {
      &-solid {
        @apply bg-info;

        &:hover {
          @apply bg-info-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply bg-info-light;
        }
      }

      &-border {
        @apply border-2 border-info text-info;

        &:hover {
          @apply text-info-dark border-info-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply text-info-light border-info-light;
        }
      }

      &-link {
        @apply text-info;

        &:hover {
          @apply text-info-dark;
        }

        &.obutton-disabled {
          @apply text-info-light;
        }
      }
    }
  }

  &-danger {
    &.obutton-type {
      &-solid {
        @apply bg-danger;

        &:hover {
          @apply bg-danger-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply bg-danger-light;
        }
      }

      &-border {
        @apply border-2 border-danger text-danger;

        &:hover {
          @apply text-danger-dark border-danger-dark;
        }

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply text-danger-light border-danger-light;
        }
      }

      &-link {
        @apply text-danger;

        &:hover {
          @apply text-danger-dark;
        }

        &.obutton-disabled {
          @apply text-danger-light;
        }
      }
    }
  }

  &-dark {
    &.obutton-type {
      &-solid {
        @apply bg-black;

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply opacity-25;
        }
      }

      &-border {
        @apply border-2 border-black text-black;

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply opacity-25;
        }
      }

      &-link {
        @apply text-black;

        &.obutton-disabled {
          @apply opacity-25;
        }
      }
    }
  }

  &-gray {
    &.obutton-type {
      &-solid {
        @apply bg-gray-500;

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply opacity-25;
        }

        .obutton-icon {
          @apply bg-gray-600;
        }
      }

      &-border {
        @apply border-2 border-gray-500 text-gray-500;

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply opacity-25;
        }

        .obutton-icon {
          @apply fill-current text-gray-500;
        }
      }

      &-link {
        @apply text-gray-500;

        &.obutton-disabled {
          @apply opacity-25;
        }

        .obutton-icon {
          @apply fill-current text-gray-500;
        }
      }
    }
  }

  &-ghost {
    @apply text-gray-500 border-gray-500 border-1;

    &:hover {
      @apply bg-gray-100;
    }
  }

  &-green {
    @apply text-white border-green-300 bg-green-300 border-1;

    &:focus {
      outline: none;
    }

    &:hover {
      @apply bg-green-400;
    }
  }

  &-dark-green {
    @apply text-white border-green-400 bg-green-400 border-1;

    &:focus {
      outline: none;
    }

    &:hover {
      @apply bg-green-500;
    }
  }

  &-faded {
    @apply text-white bg-gray-200;

    &:focus {
      outline: none;
    }
  }

  &-faded-black {
    @apply text-gray-400 bg-gray-200;

    &:focus {
      outline: none;
    }
  }

  &-white {
    &.obutton-type {
      &-solid {
        @apply bg-white text-black;

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply opacity-25;
        }

        .obutton-icon {
          @apply bg-white;
        }
      }

      &-border {
        @apply border-2 border-white text-white;

        &.obutton-disabled, &.obutton-disabled:hover {
          @apply opacity-25;
        }

        .obutton-icon {
          @apply fill-current text-white;
        }
      }

      &-link {
        @apply text-white;

        &.obutton-disabled {
          @apply opacity-25;
        }

        .obutton-icon {
          @apply fill-current text-white;
        }
      }
    }
  }

  &-size {
    &-sm {
      @apply h-9 px-5 text-md;

      .obutton-icon {
        @apply px-3;

        &.obutton-icon-right {
          @apply ml-3;
        }

        &.obutton-icon-left {
          @apply mr-3;
        }
      }
    }

    &-md {
      @apply h-10 px-7 text-lg;

      .obutton-icon {
        @apply px-5;

        &.obutton-icon-right {
          @apply ml-5;
        }

        &.obutton-icon-left {
          @apply mr-5;
        }
      }
    }
  }

  &-icon-right {
    @apply pr-0;
  }

  &-icon-left {
    @apply pl-0;
  }

  .obutton-icon {
    @apply flex items-center justify-center;
  }

  .obutton-icon-right {
    @apply rounded-r;
  }

  .obutton-icon-left {
    @apply rounded-l;
  }

  &-type {
    &-border {
      &.obutton-size {
        &-sm {
          padding: 0.2rem 1.8rem;
        }

        &-md {
          padding: 0.6rem 2.8rem
        }
      }
    }

    &-link {
      @apply px-0 underline;

      &.obutton-disabled, &.obutton-disabled:hover {
        @apply bg-transparent;
      }
    }
  }

  &-disabled {
    cursor: not-allowed;
  }

  &-gold {
    @apply rounded-none;
    background: linear-gradient(90deg, #FFF639 -82.75%, #E1AE33 144.71%);
  }

  &-error {
    @apply bg-red-error;

    &:focus {
      outline: none;
    }
  }

  &:focus {
    --ring-color: #36C0B6;
    --ring-offset-width: 2px;
    --ring-offset-color: transparent;
    --ring-width: 2px;
    --ring-padding: 0px;
    box-shadow: 0 0 0 var(--ring-offset-width) var(--ring-offset-color), 0 0 0 calc(var(--ring-offset-width) + var(--ring-width) + var(--ring-padding)) var(--ring-color) !important;
  }
}
</style>
